/**
 * Image Slider Component
 * - Desktop: semi-transparent arrows on sides
 * - Mobile: swipe gestures to navigate
 * - Dots pagination at bottom
 * - Infinite/circular navigation support
 * - Uses CachedImage for optimized loading and offline support
 */

import { useState, useRef, useCallback, forwardRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { CachedImage, preloadImages } from '@/components/ui/CachedImage';

interface ImageSliderProps {
  images: string[];
  alt?: string;
  className?: string;
  onImageChange?: (index: number) => void;
  showDots?: boolean;
  /** Enable infinite/circular navigation */
  infinite?: boolean;
  /** Called when the image area is clicked (for fullscreen mode) */
  onImageClick?: () => void;
  /** Image fit mode: 'cover' crops to fill, 'contain' shows full image */
  objectFit?: 'cover' | 'contain';
  /** Preload all images on mount for offline support */
  preload?: boolean;
}

export const ImageSlider = forwardRef<HTMLDivElement, ImageSliderProps>(function ImageSlider({
  images,
  alt = 'Image',
  className = '',
  onImageChange,
  showDots = true,
  infinite = false,
  onImageClick,
  objectFit = 'cover',
  preload = false,
}, ref) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isMobile = useIsMobile();
  const placeholderUrl = '/placeholder.svg';

  // Ensure we have at least one image
  const safeImages = images && images.length > 0 ? images : [placeholderUrl];

  // Preload all images when enabled (for offline support)
  useEffect(() => {
    if (preload && safeImages.length > 0 && safeImages[0] !== placeholderUrl) {
      preloadImages(safeImages);
    }
  }, [preload, safeImages, placeholderUrl]);

  // Touch/swipe state
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50;
  const hasMultipleImages = safeImages.length > 1;

  const goToImage = useCallback((index: number) => {
    let newIndex = index;
    
    // Handle infinite navigation
    if (infinite && hasMultipleImages) {
      if (index < 0) {
        newIndex = safeImages.length - 1;
      } else if (index >= safeImages.length) {
        newIndex = 0;
      }
    } else {
      newIndex = Math.max(0, Math.min(index, safeImages.length - 1));
    }
    
    setCurrentIndex(newIndex);
    onImageChange?.(newIndex);
  }, [safeImages.length, onImageChange, infinite, hasMultipleImages]);

  const goToPrev = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (infinite || currentIndex > 0) {
      goToImage(currentIndex - 1);
    }
  }, [currentIndex, goToImage, infinite]);

  const goToNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (infinite || currentIndex < safeImages.length - 1) {
      goToImage(currentIndex + 1);
    }
  }, [currentIndex, safeImages.length, goToImage, infinite]);

  // Touch handlers for mobile swipe
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  }, []);

  const onTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      if (infinite || currentIndex < safeImages.length - 1) {
        goToImage(currentIndex + 1);
      }
    } else if (isRightSwipe) {
      if (infinite || currentIndex > 0) {
        goToImage(currentIndex - 1);
      }
    }

    touchStartX.current = null;
    touchEndX.current = null;
  }, [currentIndex, safeImages.length, goToImage, infinite]);

  // Check if arrows should be visible (for non-infinite mode)
  const showLeftArrow = infinite || currentIndex > 0;
  const showRightArrow = infinite || currentIndex < safeImages.length - 1;

  return (
    <div 
      ref={ref}
      className={`relative w-full h-full group ${className}`}
      onTouchStart={isMobile ? onTouchStart : undefined}
      onTouchMove={isMobile ? onTouchMove : undefined}
      onTouchEnd={isMobile ? onTouchEnd : undefined}
    >
      {/* Main image - uses CachedImage for optimized loading and offline support */}
      <CachedImage
        src={safeImages[currentIndex] || placeholderUrl}
        alt={alt}
        className={`w-full h-full select-none ${
          objectFit === 'contain' ? 'object-contain' : 'object-cover'
        } ${onImageClick ? 'cursor-pointer' : ''} ${className.includes('rounded') ? className : ''}`}
        loading="eager"
        draggable={false}
        onClick={onImageClick}
        fallbackSrc={placeholderUrl}
      />

      {/* Desktop arrows - only show on hover */}
      {!isMobile && hasMultipleImages && (
        <>
          {/* Left arrow */}
          <button
            onClick={goToPrev}
            disabled={!showLeftArrow}
            className={`
              absolute left-2 top-1/2 -translate-y-1/2 z-10
              w-10 h-10 rounded-full
              bg-black/30 hover:bg-black/50
              flex items-center justify-center
              transition-all duration-200
              opacity-0 group-hover:opacity-100
              ${!showLeftArrow ? 'invisible' : 'visible'}
            `}
            aria-label="Предыдущее фото"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>

          {/* Right arrow */}
          <button
            onClick={goToNext}
            disabled={!showRightArrow}
            className={`
              absolute right-2 top-1/2 -translate-y-1/2 z-10
              w-10 h-10 rounded-full
              bg-black/30 hover:bg-black/50
              flex items-center justify-center
              transition-all duration-200
              opacity-0 group-hover:opacity-100
              ${!showRightArrow ? 'invisible' : 'visible'}
            `}
            aria-label="Следующее фото"
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </>
      )}

      {/* Pagination dots */}
      {showDots && hasMultipleImages && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
          {safeImages.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => {
                e.stopPropagation();
                goToImage(idx);
              }}
              className={`w-2 h-2 rounded-full transition-all shadow-sm ${
                idx === currentIndex 
                  ? 'bg-white scale-110' 
                  : 'bg-white/60'
              }`}
              style={{
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }}
              aria-label={`Фото ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
});

export default ImageSlider;
